import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../state/state';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
} from '@mui/material';
import { PageName } from '../../../../enums.ts';
import { Download, Delete, Assessment } from '@mui/icons-material';
import { openModal } from '../../../../state/ModalSlice/ModalSlice';
import { actions as actions_ExportsDataSlice } from '../../../../state/ExportsDataSlice/ExportsDataSlice.tsx';
import { useBackend } from '../../../../hooks/backend/useBackend.ts';
import { deleteObject, ref } from 'firebase/storage';
import { storage } from '../../../../Config/firebase';
import { DocumentData, Timestamp } from 'firebase/firestore';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { ExportType } from '../../../Common/types/ExportTypes';
import { selectActiveRole, selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2.ts';
import { GenericActions } from '../../../../state/GenericSlice.ts';
import AccessControl from '../../Components/AccessControl';

interface Export {
    RequestedOn: Timestamp;
    id: string;
    Status: string;
    exportType: string;
    StartDate: Timestamp;
    EndDate: Timestamp;
    month: string;
    year: string;
    doc_url: string;
    // ... other fields ...
}

const getDateRangeString = (exportItem: Export): string => {
    if (!exportItem.exportType || exportItem.exportType === 'Payments') {
        return `${exportItem.StartDate ? exportItem.StartDate.toString() : 'N/A'} - ${exportItem.EndDate ? exportItem.EndDate.toString() : 'N/A'}`;
    }
    
    if (exportItem.exportType === 'Attendance') {
        if (exportItem.StartDate && exportItem.EndDate) {
            return `${exportItem.StartDate.toString()} - ${exportItem.EndDate.toString()}`;
        }
        if (exportItem.month && exportItem.year) {
            return `${exportItem.month}-${exportItem.year}`;
        }
        return 'N/A';
    }
    
    return 'N/A';
};

const ExportHistory: React.FC = () => {
    // const [exports, setExports] = useState<Export[]>([]);
    const [loading, setLoading] = useState(true);
    const providerId = useAppSelector(selectEffectiveUID);
    const dispatch = useAppDispatch()
    const activeRole = useAppSelector(selectActiveRole);
    const role = activeRole?.role;
    const hasAccess = role ? role === "Administrator" : true;
    const { loadCollection, remove } = useBackend(`ServiceProviders/${providerId}/Exports`)


    const exports = useAppSelector((state) =>
        state.ExportsDataSlice.data as unknown as Export[]
    );

    // You can modify this based on your access control logic

    useEffect(() => {

        if (providerId) {
            loadCollection(actions_ExportsDataSlice as GenericActions<DocumentData>,
                {
                    sort: {
                        attribute: 'RequestedOn',
                        order: 'desc'
                    }
                }
            )
        }
        setLoading(false)

    }, [providerId, loadCollection])



    const getRowStyle = (status: string) => {
        if (status.toLowerCase() === 'success') {
            return { backgroundColor: 'rgba(76, 175, 80, 0.1)' };
        } else if (status.toLowerCase() === 'pending') {
            return { backgroundColor: 'rgba(255, 255, 224, 0.3)' };
        }
        return {};
    };

    const getStatusStyle = (status: string) => {
        if (status.toLowerCase() === 'success') {
            return { color: '#4caf50', fontWeight: 'bold' };
        } else if (status.toLowerCase() === 'pending') {
            return { color: '#FFA500', fontWeight: 'bold' };
        }
        return { fontWeight: 'bold' };
    };

    function handleOpenExportModal(exportType: ExportType) {
        let modalName = 'ExportPaymentsModalOpen';
        switch (exportType) {
            case ExportType.ATTENDANCE:
                modalName = 'ExportAttendanceModalOpen';
                break;
            case ExportType.STUDENT_PROGRESS:
                modalName = 'ExportStudentProgressModalOpen';
                break;
            default:
                modalName = 'ExportPaymentsModalOpen';
        }
        dispatch(openModal(modalName));
    }

    const handleDelete = async (exportItem: Export) => {
        try {
            // Delete the Excel file from Firebase Storage if it exists
            if (exportItem.doc_url) {
                const fileRef = ref(storage, exportItem.doc_url);
                await deleteObject(fileRef);
            }

            // Delete the document from Firestore
            await remove(exportItem.id);

            // Refresh the collection
            loadCollection(actions_ExportsDataSlice, {
                sort: {
                    attribute: 'RequestedOn',
                    order: 'desc'
                }
            });
        } catch (error) {
            console.error('Error deleting export:', error);
            // Optionally show an error message to the user
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="200px"
            >
                <CircularProgress />
            </Box>
        );
    }



    return (
        <AccessControl pageName={PageName.ExportHistory} >
            <div className='p-4'><Box>


                {/* Central Export Section */}
                <div className='my-8 text-center'>
                    <h1 className="font-semibold text-2xl mb-4">Export Order Summary</h1>
                    <p className='mb-6'>Generate and download detailed customer order reports in Excel.</p>
                    <div className='flex justify-center gap-6 mb-10'>
                        <button
                            onClick={() => handleOpenExportModal(ExportType.PAYMENTS)}
                            className={`bg-cc_brand text-white py-3 px-8 rounded-lg border-gray-500 flex justify-center items-center gap-2 cursor-pointer text-lg hover:bg-opacity-90 transition-all`}>
                            <ReceiptLongIcon /> Export Payments
                        </button>
                        <button
                            onClick={() => handleOpenExportModal(ExportType.ATTENDANCE)}
                            className={`bg-cc_brand text-white py-3 px-8 rounded-lg border-gray-500 flex justify-center items-center gap-2 cursor-pointer text-lg hover:bg-opacity-90 transition-all`}>
                            <PeopleAltIcon /> Export Attendance
                        </button>
                        <button
                            onClick={() => handleOpenExportModal(ExportType.STUDENT_PROGRESS)}
                            className={`bg-cc_brand text-white py-3 px-8 rounded-lg border-gray-500 flex justify-center items-center gap-2 cursor-pointer text-lg hover:bg-opacity-90 transition-all`}>
                            <Assessment /> Export Student Progress
                        </button>
                        <button
                            className={`bg-gray-400 text-white py-3 px-8 rounded-lg border-gray-500 flex justify-center items-center gap-2 cursor-not-allowed text-lg relative`}>
                            <ReceiptLongIcon /> Archive and Download Invoices
                            <span className="absolute -top-3 -right-3 bg-yellow-500 text-xs text-white px-2 py-1 rounded-full">Coming Soon</span>
                        </button>
                    </div>
                </div>

                {/* History Table Section */}
                <div>
                    <h2 className="font-semibold text-xl mb-4">Export History</h2>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ fontFamily: 'Arial, sans-serif', fontWeight: '600' }}>REQUESTED ON</TableCell>
                                    <TableCell style={{ fontFamily: 'Arial, sans-serif', fontWeight: '600' }}>Export Type</TableCell>
                                    <TableCell style={{ fontFamily: 'Arial, sans-serif', fontWeight: '600' }}>Export Cycle</TableCell>
                                    <TableCell style={{ fontFamily: 'Arial, sans-serif', fontWeight: '600' }}>STATUS</TableCell>
                                    <TableCell style={{ fontFamily: 'Arial, sans-serif', fontWeight: '600' }}>EXPORT ID</TableCell>
                                    <TableCell style={{ fontFamily: 'Arial, sans-serif', fontWeight: '600' }}>ORDER REPORT</TableCell>
                                    <TableCell style={{ fontFamily: 'Arial, sans-serif', fontWeight: '600' }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exports.filter(exportItem => exportItem.exportType !== 'EventPayments').map((exportItem) => (
                                    <TableRow key={exportItem.id} style={getRowStyle(exportItem.Status)}>
                                        <TableCell style={{ fontFamily: 'Arial, sans-serif' }}>
                                            {exportItem.RequestedOn ? exportItem.RequestedOn.toString() : 'N/A'}
                                        </TableCell>
                                        <TableCell style={{ fontFamily: 'Arial, sans-serif' }}>
                                            {exportItem.exportType ? exportItem.exportType.toString() : 'Payments'}
                                        </TableCell>
                                        <TableCell style={{ fontFamily: 'Arial, sans-serif' }}>
                                            {getDateRangeString(exportItem)}

                                        </TableCell>
                                        <TableCell>
                                            <span style={{
                                                ...getStatusStyle(exportItem.Status),
                                                fontFamily: 'Arial, sans-serif'
                                            }}>
                                                {exportItem.Status}
                                            </span>
                                        </TableCell>
                                        <TableCell style={{ fontFamily: 'Arial, sans-serif' }}>{exportItem.id}</TableCell>
                                        <TableCell>
                                            {exportItem.doc_url ? (
                                                <a
                                                    href={exportItem.doc_url}
                                                    download={`order-export-${exportItem.id}.xlsx`}
                                                    className='underline underline-offset-2 flex items-center gap-1 text-blue-600 hover:text-blue-800'
                                                >
                                                    <Download fontSize='small' /> Download
                                                </a>
                                            ) : exportItem.Status === 'Pending' ? <div className='flex pl-6'><CircularProgress size={20} /></div> : 'N/A'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Delete Export">
                                                <IconButton
                                                    onClick={() => handleDelete(exportItem)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

            </Box></div>
        </AccessControl>
    );
};


export default ExportHistory;
